import { useLocalStorage } from "@rehooks/local-storage";
import { isEqual } from "lodash-es";
import { useCallback, useState } from "react";

import useAnalytics, { AnalyticsPage } from "hooks/useAnalytics";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import { SearchQueryRequestedPage } from "types/generated";

import { UsePaginationFetchMoreProps } from "./types";

type UsePaginationProps = {
  fetchItems: (props: UsePaginationFetchMoreProps) => Promise<void> | void;
  startCursor?: string;
  endCursor?: string;
  initialLimit: number;
  allItemsCount: number;
  visibleItemsCount?: number;
  storageKey: string;
  analyticsPage?: AnalyticsPage;
  analyticsProps?: TrackAnalyticsEventProperties;
  resetKey?: unknown;
  loading?: boolean;
};

const INITIAL_PAGE = 1;

const usePagination = ({
  fetchItems,
  startCursor,
  endCursor,
  initialLimit,
  allItemsCount,
  visibleItemsCount,
  storageKey,
  analyticsPage,
  analyticsProps,
  resetKey,
  loading = false,
}: UsePaginationProps) => {
  const trackAnalytics = useAnalytics({
    page: analyticsPage,
    defaultCallbackTrackProperties: analyticsProps,
  });

  const [rowsPerPage, setRowsPerPage] = useLocalStorage<number>(storageKey, initialLimit);

  const [page, setPage] = useState<number>(INITIAL_PAGE);
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);
  const [isRangePending, setIsRangePending] = useState(false);

  if (!isEqual(previousResetKey, resetKey)) {
    setPreviousResetKey(resetKey);
    setPage(INITIAL_PAGE);
    setIsRangePending(true);
  }

  /**
   * Right after a reset the page is back to 1 while the items count still belongs to the previous
   * query, so the range would read as a wrong "1 - N of <stale total>". Hide it until the caller
   * stops loading — and only until then, so a caller that never reports `loading` gets its range
   * back on the next render instead of losing it for good.
   */
  if (isRangePending && !loading) {
    setIsRangePending(false);
  }

  const rangeTotal = isRangePending ? 0 : allItemsCount || visibleItemsCount || 0;

  const from = rangeTotal === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  // TODO: refactor when new API with count provided
  const to = Math.min(page * rowsPerPage, rangeTotal);
  const lastPage = Math.ceil(allItemsCount / rowsPerPage);

  const goToNextPage = useCallback(async () => {
    if (endCursor) {
      const newPage = page + 1;
      setIsRangePending(false);
      setPage(newPage);

      await fetchItems({
        cursor: endCursor,
        rowsPerPage,
        requestedPage: SearchQueryRequestedPage.Next,
      });
    }
  }, [fetchItems, endCursor, page, rowsPerPage]);

  const goToPrevPage = useCallback(async () => {
    if (startCursor) {
      const newPage = page - 1;
      setIsRangePending(false);
      setPage(newPage);

      await fetchItems({
        cursor: newPage === 1 ? null : startCursor,
        rowsPerPage,
        requestedPage:
          newPage === 1 ? SearchQueryRequestedPage.First : SearchQueryRequestedPage.Previous,
      });
    }
  }, [fetchItems, rowsPerPage, page, startCursor]);

  const updateRowsPerPage = useCallback(
    async (rows: number) => {
      trackAnalytics?.("Rows per page changed", {
        rowCountSelected: rows,
      });
      setIsRangePending(false);
      setRowsPerPage(rows);
      setPage(INITIAL_PAGE);

      await fetchItems({
        cursor: null,
        rowsPerPage: rows,
        requestedPage: SearchQueryRequestedPage.First,
      });
    },
    [fetchItems, setRowsPerPage, trackAnalytics]
  );

  const goToLastPage = useCallback(async () => {
    setIsRangePending(false);
    setPage(lastPage);

    await fetchItems({
      cursor: null,
      rowsPerPage,
      requestedPage: SearchQueryRequestedPage.Last,
    });
  }, [fetchItems, lastPage, rowsPerPage]);

  const goToFirstPage = useCallback(async () => {
    setIsRangePending(false);
    setPage(INITIAL_PAGE);

    await fetchItems({
      cursor: null,
      rowsPerPage,
      requestedPage: SearchQueryRequestedPage.First,
    });
  }, [fetchItems, rowsPerPage]);

  return {
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
    setRowsPerPage: updateRowsPerPage,
    from,
    to,
    rowsPerPage,
    allItemsCount,
    lastPage,
    page,
  };
};

export default usePagination;
